import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import { Pencil } from "lucide-react";
import { getAgentById } from "../../../../services/agent_management_service.js";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";

const getTranslatedValue = (value) => {
  if (!value) return "-";
  if (typeof value === 'string') return value;
  
  const lang = localStorage.getItem('i18nextLng') || 'en';
  return value[lang] || value.en || value.dr || value.ps || "-";
};

const SectionCard = ({ title, onEdit, children }) => {
  const { t } = useTranslation();
  
  return (
    <div className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm transition-colors duration-200">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
          {t(title) || title}
        </h2>
        <Button
          variant="outline"
          size="sm"
          className="gap-1 px-3 text-sm"
          onClick={onEdit}
          sx={{
            color: '#4b5563',
            borderColor: '#e5e7eb',
            '&:hover': {
              backgroundColor: '#f3f4f6',
              borderColor: '#d1d5db',
            },
            '.dark &': {
              color: '#9ca3af',
              borderColor: '#374151',
              '&:hover': {
                backgroundColor: '#1f2937',
                borderColor: '#4b5563',
              },
            }
          }}
        >
          <Pencil className="w-4 h-4" /> {t('common.edit') || 'Edit'}
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-700 dark:text-gray-300">
        {children}
      </div>
    </div>
  );
};

const LabelValue = ({ label, value }) => {
  const { t } = useTranslation();
  
  return (
    <div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
        {t(label) || label}
      </div>
      <div className="font-medium text-gray-800 dark:text-white">
        {getTranslatedValue(value)}
      </div>
    </div>
  );
};

const AgentProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [agentData, setAgentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const IMG_BASE_URL = import.meta.env.VITE_IMG_BASE_URL;
  
  useEffect(() => {
    const fetchAgentData = async () => {
      try {
        setLoading(true);
        const response = await getAgentById(id);
        console.log("this is single agent data", response);
        setAgentData(response); 
        setLoading(false);
      } catch (error) {
        console.error("Error fetching agent:", error);
        setLoading(false);
      }
    };

    fetchAgentData();
  }, [id]);

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'suspended':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
        <CircularProgress sx={{ color: '#dc2626' }} />
      </div>
    );
  }

  if (!agentData) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
        <div className="text-center py-10 text-gray-600 dark:text-gray-400">
          {t('agentProfile.notFound') || 'Agent not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-5 py-4 min-h-screen h-auto bg-gradient-to-br from-[#EFF2FF] via-[#FAF5FF] to-[#FCF3FB] dark:from-[#1e293b] dark:via-[#334155] dark:to-[#0f172a] transition-colors duration-200">
      <div className="max-w-5xl mx-auto space-y-6 px-6 py-6 rounded-2xl border border-[#E4E7EC] dark:border-gray-700 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
        {/* Profile Header Card */}
        <div className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm transition-colors duration-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <img
                src={`${IMG_BASE_URL}${agentData?.user?.profilePicture}`}
                alt="profile"
                className="w-16 h-16 rounded-full object-cover border-2 border-secondary-200 dark:border-secondary-700"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/64?text=User';
                }}
              />
              <div>
                <div className="text-lg font-semibold text-gray-800 dark:text-white">
                  {agentData.user?.username}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('agentProfile.id') || 'ID'}: {agentData.user?.id} | {getTranslatedValue(agentData.districtDetails?.districtName)}
                </div>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="gap-1 px-3 text-sm"
              onClick={() => navigate(`/edit-agent/${id}`)}
              sx={{
                color: '#4b5563',
                borderColor: '#e5e7eb',
                '&:hover': {
                  backgroundColor: '#f3f4f6',
                  borderColor: '#d1d5db',
                },
                '.dark &': {
                  color: '#9ca3af',
                  borderColor: '#374151',
                  '&:hover': {
                    backgroundColor: '#1f2937',
                    borderColor: '#4b5563',
                  },
                }
              }}
            >
              <Pencil className="w-4 h-4" /> {t('common.edit') || 'Edit'}
            </Button>
          </div>
        </div>

        <SectionCard title="agentProfile.personalInfo">
          <LabelValue label="agentProfile.username" value={agentData.user?.username} />
          <LabelValue label="agentProfile.email" value={agentData.user?.email} />
          <LabelValue label="agentProfile.phone" value={agentData.user?.mobileNumber} />
          <LabelValue
            label="agentProfile.alternativeContact"
            value={agentData.alternativeContact}
          />
        </SectionCard>

        <SectionCard title="agentProfile.address">
          <LabelValue 
            label="agentProfile.country" 
            value={agentData.countryDetails?.countryName} 
          />
          <LabelValue 
            label="agentProfile.province" 
            value={agentData.provinceDetails?.provinceName} 
          />
          <LabelValue 
            label="agentProfile.district" 
            value={agentData.districtDetails?.districtName} 
          />
          <LabelValue label="agentProfile.fullAddress" value={agentData.address} />
        </SectionCard>

        <SectionCard title="agentProfile.accountInfo">
          <LabelValue 
            label="agentProfile.accountType" 
            value={agentData.accountType} 
          />
          <LabelValue 
            label="agentProfile.registrationType" 
            value={agentData.registrationType} 
          />
          <LabelValue 
            label="agentProfile.messageLanguage" 
            value={agentData.messageLanguage} 
          />
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              {t('agentProfile.status') || 'Status'}
            </div>
            <div className="font-medium">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(agentData.user?.status)}`}>
                {t(`status.${agentData.user?.status?.toLowerCase()}`) || agentData.user?.status}
              </span>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export default AgentProfile;